import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";
import { clampPopoverLeft } from "@/utils/clampPopoverLeft";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";
import { LinkedinTier, linkedinTierLabel } from "@/utils/getLinkedinTier";

import { IdentityTagChip, SenderIdentityAvatar } from "./SenderIdentityToken";

export interface SenderRecipientOption {
	id: string;
	label: string;
	type: "email" | "linkedin";
	icon?: JSX.Element;
	userId?: number;
	emailId?: number;
	emailAddress?: string;
	value?: string;
	disabled?: boolean;
	disabledReason?: string;
	/** An i18n key for the reason, translated where it is shown (wins over `disabledReason`). */
	disabledReasonKey?: string;
	linkedinTier?: LinkedinTier;
	/** The i18n key of a LinkedIn identity row's channel badge (set by `buildSenderOptions`). */
	channelBadgeKey?: string;
	/**
	 * Rich sender-identity fields (set by `buildSenderOptions`): when
	 * `displayName` is present, the option renders as an identity row (avatar
	 * initials + channel badge + Primary/Secondary tag + address sub-line)
	 * and the trigger becomes the compact identity token. Recipient options
	 * omit them and keep the plain label rendering.
	 */
	displayName?: string;
	/** Email alias tag, from the mailbox's `type` column ("primary" | "secondary"). */
	tag?: "primary" | "secondary";
	/** Secondary line under the name — the email address for email identities. */
	sublabel?: string;
}

interface SenderRecipientDropdownProps {
	value: string;
	options: SenderRecipientOption[];
	onSelect: (option: SenderRecipientOption) => void;
	icon?: JSX.Element;
	/**
	 * When true, LinkedIn options additionally display the sender's LinkedIn tier
	 * (Free / Premium / Sales Navigator) as a muted second line. Free senders also
	 * show an amber warning. Intended for LinkedIn invite-note context only.
	 */
	showLinkedinTier?: boolean;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

/** Mirrors the menu's `min-w-[300px]`: the floor used before the menu has been measured. */
const MENU_MIN_WIDTH = 300;

export const SenderRecipientDropdown: FunctionComponent<SenderRecipientDropdownProps> = ({
	value,
	options,
	onSelect,
	icon,
	showLinkedinTier,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	// `null` until measured. The menu is always rendered `position: fixed` (the `fixed` class below) and
	// stays `visibility: hidden` until positioned, so it is out of the layout flow from the very first
	// render and can never displace the trigger it measures against — mirrors the sibling
	// SenderSelect/ChannelSelect. This was latent while the menu portalled to `document.body`; now that
	// the Lead Panel portals it into the panel's `flex flex-col` `<aside>`, an in-flow first render
	// shifted the "From/To" trigger and threw off the first-open position (PREC-3627 follow-up).
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const estimatedMenuHeight = Math.min(options.length * 36 + 8, 300);
		const openUpward = spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;
		// The From/To menu is the widest thing this row opens (300px floor), and the Lead Panel is
		// flush against the viewport's right edge — so anchoring it to the trigger's left alone puts
		// it off-screen exactly like the timing calendar did (PREC-4130). `width: max-content` below
		// keeps this measurement honest: shrink-to-fit would report the space left of the edge.
		const menuWidth = Math.max(menuRef.current?.offsetWidth ?? 0, MENU_MIN_WIDTH);
		const left = clampPopoverLeft(rect.left, menuWidth, window.innerWidth);

		if (openUpward) {
			setMenuStyle({
				bottom: window.innerHeight - rect.top + 4,
				left,
				width: "max-content",
				maxHeight: Math.min(rect.top - 8, 300),
			});
		} else {
			setMenuStyle({
				top: rect.bottom + 4,
				left,
				width: "max-content",
				maxHeight: Math.min(spaceBelow - 8, 300),
			});
		}
	}, [options.length]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// In the extension the menu lives in a shadow root, where a document listener sees
			// `event.target` retargeted to the host — every press inside would read as outside,
			// close the menu on mousedown and swallow the option's click (PREC-4296).
			const target = getComposedEventTarget(event);

			if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
				return;
			}

			setIsOpen(false);
		};

		if (isOpen) {
			updatePosition();
			document.addEventListener("mousedown", handleClickOutside);
			window.addEventListener("resize", updatePosition);
			window.addEventListener("scroll", updatePosition, true);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen, updatePosition]);

	const menu = useMemo(() => {
		if (!isOpen) {
			return null;
		}

		return ReactDOM.createPortal(
			<div
				ref={menuRef}
				className="fixed bg-white border border-border-light rounded-lg shadow-lg py-1 min-w-[300px] overflow-y-auto z-[9999]"
				style={menuStyle ?? { visibility: "hidden", width: "max-content" }}
			>
				{options.map((option) => {
					const showTier = showLinkedinTier && option.type === "linkedin" && !!option.linkedinTier;
					const isFreeTier = option.linkedinTier === "free";
					const tierLine = showTier ? (
						isFreeTier ? (
							<Tooltip content={t("linkedin.tier.freeWarning")} direction="top">
								<span className="text-[10px] text-amber-600 font-medium cursor-help">
									{linkedinTierLabel(option.linkedinTier!, t)}
								</span>
							</Tooltip>
						) : (
							<span className="text-[10px] text-text-meta">
								{linkedinTierLabel(option.linkedinTier!, t)}
							</span>
						)
					) : null;

					const isSelected = value === option.label && !option.disabled;

					const button = (
						<button
							key={option.id}
							className={classNames(
								"w-full px-3 py-2 text-left text-sm flex items-center gap-2 cursor-pointer",
								option.disabled
									? "text-text-meta cursor-not-allowed opacity-60"
									: "hover:bg-brand-white",
								isSelected ? "bg-brand-white font-medium" : ""
							)}
							disabled={option.disabled}
							onClick={(e) => {
								e.stopPropagation();

								if (option.disabled) {
									return;
								}

								onSelect(option);
								setIsOpen(false);
							}}
						>
							{option.icon && <span className="flex items-center">{option.icon}</span>}
							{option.displayName ? (
								/* Rich identity row: avatar + channel badge, name + alias tag, address sub-line. */
								<>
									<SenderIdentityAvatar channel={option.type} name={option.displayName} />
									<span className="flex flex-col min-w-0 flex-1">
										<span className="flex items-center gap-1.5 min-w-0">
											<span className="truncate">{option.displayName}</span>
											{option.tag && <IdentityTagChip tag={option.tag} />}
											{option.type === "linkedin" && (
												<span className="text-[10px] text-text-meta">
													{option.channelBadgeKey
														? t(option.channelBadgeKey)
														: option.label.includes("will send invite")
															? t("inbox.identity.linkedinInvite")
															: t("inbox.identity.linkedin")}
												</span>
											)}
										</span>
										{option.sublabel && (
											<span className="truncate text-[11px] text-text-meta">{option.sublabel}</span>
										)}
										{tierLine}
									</span>
									{isSelected && (
										<Icon className="w-3 h-3 shrink-0 text-blue-action" icon={faCheck} />
									)}
								</>
							) : (
								<span className="flex flex-col min-w-0">
									<span className="truncate">{option.label}</span>
									{tierLine}
								</span>
							)}
						</button>
					);

					const disabledReason = option.disabledReasonKey
						? t(option.disabledReasonKey)
						: option.disabledReason;

					if (option.disabled && disabledReason) {
						return (
							<Tooltip key={option.id} content={disabledReason}>
								{button}
							</Tooltip>
						);
					}

					return button;
				})}
			</div>,
			portalContainer ?? document.body
		);
	}, [isOpen, menuStyle, options, value, onSelect, showLinkedinTier, t, portalContainer]);

	// The compact identity token replaces the plain-label trigger when the
	// selected option carries rich sender-identity fields (from-pickers only;
	// recipient options never set `displayName`).
	const selectedOption = options.find((option) => option.label === value);

	return (
		<div className="relative inline-block">
			<button
				ref={triggerRef}
				className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-blue-action font-medium border border-blue-action/20 bg-blue-action/10 hover:bg-blue-action/20 rounded transition-colors cursor-pointer"
				title={selectedOption?.displayName ? value : undefined}
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
			>
				{icon && <span className="flex items-center">{icon}</span>}
				{selectedOption?.displayName ? (
					<span className="inline-flex items-center gap-1.5 py-0.5">
						<SenderIdentityAvatar
							channel={selectedOption.type}
							className="!h-5 !w-5"
							name={selectedOption.displayName}
						/>
						{selectedOption.tag ? (
							<IdentityTagChip tag={selectedOption.tag} />
						) : (
							<span className="max-w-[120px] truncate">{selectedOption.displayName}</span>
						)}
					</span>
				) : (
					<span className="max-w-fit truncate">{value}</span>
				)}
				<Icon className="w-2 h-2 text-blue-action/60" icon={faChevronDown} />
			</button>

			{menu}
		</div>
	);
};
